import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import Save from '@mui/icons-material/Save';
import CommonInput from '../../component/form/common-input.component';
import { SelectOption } from '../../component/form/base-input.component';
import DialogComponent from '../../component/dialog/dialog.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import i18n from '../../i18n';

/** 西班牙 Fiskaly 区域下拉项（与 RBP2 `Fiskaly_Territory` 一致） */
const FISKALY_TERRITORY_ITEMS: SelectOption[] = [
    { value: 'SPAIN_OTHER', label: 'SPAIN_OTHER(Verifactu)' },
    { value: 'CANARY_ISLANDS', label: 'CANARY_ISLANDS(Verifactu)' },
    { value: 'CEUTA', label: 'CEUTA(Verifactu)' },
    { value: 'MELILLA', label: 'MELILLA(Verifactu)' },
    { value: 'ARABA', label: 'ARABA(TicketBAI)' },
    { value: 'BIZKAIA', label: 'BIZKAIA(TicketBAI)' },
    { value: 'GIPUZKOA', label: 'GIPUZKOA(TicketBAI)' },
    { value: 'Na_Ticket', label: 'Na_Ticket' },
];

/** 国家 →税务字段展示名（与 RBP2 `mapCtyTaxInfo` 对齐的常用项） */
const MAP_CTY_TAX_LABEL: Record<string, Record<string, string>> = {
    France: {
        CodiceFiscale: 'Code Fiscal',
        IdCodice: 'Numéro de TVA',
        Denominazione: 'Dénomination',
        CAP: 'Code Postal',
        Indirizzo: 'Adresse',
        Comune: 'Ville',
        Provincia: 'Province',
    },
    Spain: {
        CodiceFiscale: 'NIF',
        IdCodice: 'CIF',
        Denominazione: 'Razón Social',
        CAP: 'Código Postal',
        Indirizzo: 'Dirección',
        Comune: 'Ciudad',
        Provincia: 'Provincia',
    },
};

export type DisRestaurantTaxFormValues = Record<string, string | number | undefined>;

type DisRestaurantTaxDrawerProps = {
    /** 是否打开弹窗 */
    open: boolean;
    /** 关闭弹窗 */
    onClose: () => void;
    /** 国家名称（与 `get_restaurant_info` 的 `name` 一致） */
    country: string;
    /** 是否西班牙门店 */
    isSpain: boolean;
    /** 是否新增（否则为编辑） */
    isAdd: boolean;
    /** 初始表单数据 */
    detail: DisRestaurantTaxFormValues;
    /** 保存中（与底部主按钮 `LoadingOverlayButton` 同步，由父组件在 `onSave` 请求期间置 true） */
    saveSubmitting: boolean;
    /** 保存（非西班牙新增/编辑时触发） */
    onSave: (data: DisRestaurantTaxFormValues) => void;
};

/** 与 `call-service.page` 底部主按钮同风格的 `sx` */
const primaryFooterButtonSx = {
    borderRadius: '8px',
    height: 56,
    textTransform: 'none' as const,
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '24px',
    boxShadow: 'none',
    gap: 1,
    '&:hover': { boxShadow: 'none' },
};

/** 与 `call-service.page` 中「更多设置」弹窗同一套纸面/边框样式 */
const DIALOG_SX = {
    '& .MuiPaper-root.dis-restaurant-tax-dialog__paper': {
        background: 'var(--fill-neutral-100, #fff)',
        backgroundImage: 'none',
        border: '1px solid rgba(31, 41, 64, 0.12)',
    },
} as const;

/**
 * DIS 餐厅税务信息新增/编辑/查看弹窗：字段与 RBP2 `CommomSettingDetail` REST 分支一致；版式对齐呼叫服务编辑弹窗。
 */
const DisRestaurantTaxDrawer: React.FC<DisRestaurantTaxDrawerProps> = ({
    open,
    onClose,
    country,
    isSpain,
    isAdd,
    detail,
    saveSubmitting,
    onSave,
}) => {
    /** 当前编辑中的表单 */
    const [form, setForm] = useState<DisRestaurantTaxFormValues>({});

    useEffect(() => {
        if (!open) {
            return;
        }
        setForm({ ...detail });
    }, [open, detail]);

    /**
     * 更新单个字段。
     * @param key - 字段名
     * @param val - 新值
     */
    const setField = (key: string, val: string): void => {
        setForm((prev) => ({ ...prev, [key]: val }));
    };

    /** 西班牙门店仅查看，不可保存 */
    const readOnlySpain = isSpain;
    /** 是否展示保存按钮 */
    const showSave = !readOnlySpain;

    /**
     * 取字段标签：优先国家映射，否则用 i18n key 或字段名。
     * @param field - 字段 key
     */
    const labelFor = (field: string): string => {
        const mapped = MAP_CTY_TAX_LABEL[country]?.[field];
        if (mapped) {
            return mapped;
        }
        if (field === 'CodiceFiscale') {
            return 'Codice Fiscale';
        }
        if (field === 'IdCodice') {
            return 'P.IVA';
        }
        return field;
    };

    /** 顶栏主标题：新增与详情文案（与呼叫服务弹窗主标题同字号） */
    const headerTitle = isAdd ? i18n.t('dis_setting_page_add_tax_info') : i18n.t('google_text_detail');

    /** 与 MUI `Dialog` / 公共头 `onClose` 签名对齐 */
    const handleClose = () => onClose();

    /** 提交时由父级展示 loading，此处仅触发父级 `onSaveTax` */
    const handleSave = () => onSave(form);

    // 关窗走顶栏关闭；底栏仅「可编辑」时保留全宽保存主按钮，西语只读无底栏
    return (
        <DialogComponent
            open={open}
            fullWidth={false}
            maxWidth={false}
            scroll="paper"
            className="dis-restaurant-tax-dialog"
            onClose={handleClose}
            sx={DIALOG_SX}
            PaperProps={{
                className: 'dis-restaurant-tax-dialog__paper',
                sx: {
                    width: 768,
                    maxWidth: 'calc(100vw - 48px)',
                    borderRadius: '16px',
                    boxSizing: 'border-box',
                },
            }}
            defaultHeaderProps={{
                titleContent: (
                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 700,
                            fontSize: 20,
                            lineHeight: '24px',
                            color: 'primary.main',
                        }}
                    >
                        {headerTitle}
                    </Typography>
                ),
                onClose: handleClose,
            }}
            dialogContentStyle={{
                paddingTop: 24,
                paddingLeft: 16,
                paddingRight: 16,
                paddingBottom: 0,
                maxHeight: 'min(72vh, 800px)',
                overflow: 'auto',
            }}
            footer={
                showSave ? (
                    <Box className="dis-restaurant-tax-dialog__footer" sx={{ width: '100%', px: 2, pb: 3 }}>
                        <LoadingOverlayButton
                            type="button"
                            color="primary"
                            variant="contained"
                            fullWidth
                            disableElevation
                            loading={saveSubmitting}
                            loadingIndicatorSize={24}
                            onClick={() => void handleSave()}
                            startIcon={<Save sx={{ fontSize: 24 }} aria-hidden />}
                            sx={primaryFooterButtonSx}
                        >
                            {isAdd ? i18n.t('google_text_save_add') : i18n.t('global_btn_save')}
                        </LoadingOverlayButton>
                    </Box>
                ) : undefined
            }
        >
            <Box sx={{ pb: 3, width: '100%' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('CodiceFiscale')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.CodiceFiscale ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('CodiceFiscale', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('IdCodice')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.IdCodice ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('IdCodice', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('Denominazione')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.Denominazione ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('Denominazione', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('CAP')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.CAP ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('CAP', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('Indirizzo')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.Indirizzo ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('Indirizzo', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('Comune')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.Comune ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('Comune', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {labelFor('Provincia')}
                        </Typography>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            fullWidth
                            disabled={readOnlySpain}
                            value={String(form.Provincia ?? '')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('Provincia', e.target.value)}
                        />
                    </Grid>
                    {isSpain ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Ciudad
                                </Typography>
                                <CommonInput
                                    inputType="INPUT"
                                    size="small"
                                    fullWidth
                                    disabled={readOnlySpain}
                                    value={String(form.municipality ?? '')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('municipality', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Número de calle
                                </Typography>
                                <CommonInput
                                    inputType="INPUT"
                                    size="small"
                                    fullWidth
                                    disabled={readOnlySpain}
                                    value={String(form.street_number ?? '')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('street_number', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Territorio
                                </Typography>
                                <CommonInput
                                    inputType="SELECT"
                                    size="small"
                                    fullWidth
                                    disabled={readOnlySpain}
                                    items={FISKALY_TERRITORY_ITEMS}
                                    value={String(form.territory ?? '')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('territory', e.target.value)}
                                />
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </Box>
        </DialogComponent>
    );
};

export default DisRestaurantTaxDrawer;
